import SliderField from '@pages/Admin/CourseSettings/components/SliderField/SliderField'
import cn from './index.module.css'
import CheckboxLabel from '@pages/Admin/CourseSettings/components/CheckboxLabel/CheckboxLabel'
import { ReactNode, useMemo } from 'react'
import { declOfNum } from '@/helpers/declOfNum'
import FieldWrapper from '@pages/Admin/CourseSettings/components/FieldWrapper/FieldWrapper'
import ShowTariffValue, {
  TariffPeriodUnit,
} from '../ShowTariffValue/ShowTariffValue'

const getPositiveNumbers = (values?: Array<number | null | undefined>) =>
  (values ?? []).filter(
    (item): item is number => Number.isFinite(item) && Number(item) > 0,
  )

export const isLessonCountTariff = (tariff?: ICourseTariff | null): boolean =>
  ['hour', 'group_per_lesson'].includes(tariff?.type ?? '')

const SelectTariffValue = ({
  tariff,
  demo,
  value,
  canManage = true,
  setValue,
  onChangeDemo,
  periodUnit,
}: {
  tariff: ICourseTariff
  demo: boolean
  value: number
  canManage?: boolean
  periodUnit?: TariffPeriodUnit
  setValue: (value: number) => void
  onChangeDemo: (value: boolean) => void
}) => {
  const isWeeklyTariffTextType = tariff?.weekly_tariff_text_type == 1
  const streamCustomWeeksText = isWeeklyTariffTextType
    ? 'Выберите количество занятий'
    : 'Выберите количество недель'

  const getSliderLabel = (): string => {
    switch (tariff?.type) {
      case 'group_per_lesson':
      case 'hour':
        return `Выберите количество уроков (по ${
          tariff?.lesson_duration ?? 60
        } мин.)`
      case 'minute':
        return 'Выберите количество минут'
      case 'month':
        return 'Выберите количество месяцев'
      case 'stream_custom_period':
        return 'Выберите количество месяцев'
      case 'stream_custom_days':
        return 'Выберите количество дней'
      case 'stream_custom_weeks':
        return streamCustomWeeksText

      default:
        return ''
    }
  }

  const getSliderMax = (): number => {
    switch (tariff?.type) {
      case 'group_per_lesson':
      case 'hour':
        return 24
      case 'minute':
        return 3600
      case 'month':
        return 24
      case 'stream_custom_period':
        return Math.max(
          ...(tariff?.tariffPeriods?.map((item) => item.months) ?? [0]),
        )
      case 'stream_custom_days':
        return Math.max(
          ...(tariff?.tariffDays?.map((item) => item.days) ?? [0]),
        )
      case 'stream_custom_weeks':
        return Math.max(
          ...(tariff?.tariffWeeks?.map((item) => item.weeks) ?? [0]),
        )

      default:
        return 64
    }
  }

  const getSliderMarks = () => {
    if (tariff?.type === 'group_per_lesson' || (['hour', 'minute'].includes(tariff?.type) && tariff.is_custom_tariff_values)) {
      return (
        tariff?.custom_tariff_values?.reduce<Record<number, string>>(
          (acc, item) => {
            acc[item.value] = item.value?.toString()
            return acc
          },
          {},
        ) ?? []
      )
    }

    if (tariff?.type === 'stream_custom_period') {
      return (
        tariff?.tariffPeriods?.reduce<Record<number, string>>((acc, item) => {
          acc[item.months] = item.months?.toString()
          return acc
        }, {}) ?? []
      )
    }
    if (tariff?.type === 'stream_custom_days') {
      return (
        tariff?.tariffDays?.reduce<Record<number, string>>((acc, item) => {
          acc[item.days] = item.days?.toString()
          return acc
        }, {}) ?? []
      )
    }
    if (tariff?.type === 'stream_custom_weeks') {
      return (
        tariff?.tariffWeeks?.reduce<Record<number, string>>((acc, item) => {
          acc[item.weeks] = item.weeks?.toString()
          return acc
        }, {}) ?? []
      )
    }

    return undefined
  }

  const getPeriodsMax = () => {
    if (
      tariff?.type === 'group_per_lesson' ||
      (['hour', 'minute'].includes(tariff?.type) &&
        tariff.is_custom_tariff_values)
    ) {
      if (tariff.is_custom_tariff_values) {
        return Math.max(
          ...(tariff?.custom_tariff_values?.map(
            (item) => item?.value,
          ) ?? [0]),
        )
      }

      return 1
    }

    if (tariff?.type === 'stream_custom_period') {
      return Math.max(
        ...(tariff?.tariffPeriods?.map((item) => item?.months) ?? [0]),
      )
    }
    if (tariff?.type === 'stream_custom_days') {
      return Math.max(...(tariff?.tariffDays?.map((item) => item?.days) ?? [0]))
    }
    if (tariff?.type === 'stream_custom_weeks') {
      return Math.max(
        ...(tariff?.tariffWeeks?.map((item) => item?.weeks) ?? [0]),
      )
    }

    return 0
  }

  const getPeriodsMin = () => {
    if (
      tariff?.type === 'group_per_lesson' ||
      (['hour', 'minute'].includes(tariff?.type) && tariff.is_custom_tariff_values)
    ) {
      if (!!tariff.is_custom_tariff_values) {
        return Math.min(
          ...(tariff?.custom_tariff_values?.map((item) => item?.value) ?? [0]),
        )
      } else {
        return 1
      }
    }

    if (tariff?.type === 'stream_custom_period') {
      return Math.min(
        ...(tariff?.tariffPeriods?.map((item) => item.months) ?? [0]),
      )
    }
    if (tariff?.type === 'stream_custom_days') {
      return Math.min(...(tariff?.tariffDays?.map((item) => item?.days) ?? [0]))
    }
    if (tariff?.type === 'stream_custom_weeks') {
      return Math.min(
        ...(tariff?.tariffWeeks?.map((item) => item?.weeks) ?? [0]),
      )
    }

    return 0
  }

  const demoValue = useMemo(() => {
    return `${tariff?.demo_days ?? 0} ${declOfNum(tariff?.demo_days, [
      'день',
      'дня',
      'дней',
    ])} демо`
  }, [])

  const hasOnePeriod = () => {
    if (
      tariff?.type === 'group_per_lesson' ||
      (['hour', 'minute'].includes(tariff?.type) && tariff.is_custom_tariff_values)
    ) {
      return tariff?.custom_tariff_values?.length === 1
    }

    switch (tariff?.type) {
      case 'stream_custom_period':
        return tariff?.tariffPeriods?.length === 1
      case 'stream_custom_days':
        return tariff?.tariffDays?.length === 1
      case 'stream_custom_weeks':
        return tariff?.tariffWeeks?.length === 1

      default:
        return false
    }
  }

  const getOnePeriodValue = () => {
    if (
      tariff?.type === 'group_per_lesson' ||
      (['hour', 'minute'].includes(tariff?.type) && tariff.is_custom_tariff_values)
    ) {
      const lessons = tariff?.custom_tariff_values?.[0].value ?? 0
      return `${lessons} ${declOfNum(lessons, [
        'занятие',
        'занятия',
        'занятий',
      ])}`
    }

    switch (tariff?.type) {
      case 'stream_custom_period':
        return `${tariff?.tariffPeriods?.[0]?.months ?? 0} месяцев`
      case 'stream_custom_days':
        return `${tariff?.tariffDays?.[0]?.days ?? 0} дней`
      case 'stream_custom_weeks':
        return `${tariff?.tariffWeeks?.[0]?.weeks ?? 0} недель`

      default:
        return false
    }
  }

  const getCheckboxValues = (): Array<{
    label: ReactNode
    value: string
  }> => {
    const formatCheckboxLabel = (
      name: string | undefined,
      period: string,
      price: number | undefined,
      oldPrice: number | string | null | undefined,
    ) => {
      const label = [name, period, `${price ?? 0} рублей`]
        .filter(Boolean)
        .join(', ')
      const hasOldPrice =
        oldPrice !== null && oldPrice !== undefined && oldPrice !== ''

      return (
        <>
          {label}
          {hasOldPrice && (
            <>
              , <span className={cn.oldPrice}>{oldPrice} рублей</span>
            </>
          )}
        </>
      )
    }

    switch (tariff?.type) {
      case 'stream_custom_period':
        return (
          tariff?.tariffPeriods?.map((item) => {
            return {
              label: formatCheckboxLabel(
                item?.name,
                `${item?.months} ${declOfNum(item?.months, [
                  'месяц',
                  'месяца',
                  'месяцев',
                ])}`,
                item?.price,
                item?.old_price,
              ),
              value: item.months?.toString(),
            }
          }) ?? []
        )

      case 'stream_custom_days':
        return (
          tariff?.tariffDays?.map((item) => {
            return {
              label: formatCheckboxLabel(
                item?.name,
                `${item?.days} ${declOfNum(item?.days, [
                  'день',
                  'дня',
                  'дней',
                ])}`,
                item?.price,
                item?.old_price,
              ),
              value: item?.days?.toString(),
            }
          }) ?? []
        )
      case 'stream_custom_weeks':
        return (
          tariff?.tariffWeeks?.map((item) => {
            return {
              label: formatCheckboxLabel(
                item?.name,
                `${item?.weeks} ${declOfNum(item?.weeks, [
                  isWeeklyTariffTextType ? 'занятие' : 'неделя',
                  isWeeklyTariffTextType ? 'занятия' : 'недели',
                  isWeeklyTariffTextType ? 'занятий' : 'недель',
                ])}`,
                item?.price,
                item?.old_price,
              ),
              value: item?.weeks?.toString(),
            }
          }) ?? []
        )
    }
    return []
  }

  const isChecked = (value: number, index: number): boolean => {
    if (
      tariff?.type === 'group_per_lesson' ||
      (['hour', 'minute'].includes(tariff?.type) && tariff.is_custom_tariff_values)
    ) {
      return tariff?.custom_tariff_values?.[index]?.value === value
    }

    switch (tariff?.type) {
      case 'stream_custom_period':
        return tariff?.tariffPeriods?.[index]?.months === value

      case 'stream_custom_days':
        return tariff?.tariffDays?.[index]?.days === value

      case 'stream_custom_weeks':
        return tariff?.tariffWeeks?.[index]?.weeks === value
    }
    return false
  }

  if (!canManage) {
    return <ShowTariffValue tariff={tariff} value={value} periodUnit={periodUnit} />
  }

  return (
    <div className={cn.wrapper}>
      {demo ? (
        <FieldWrapper label="Длительность">
          <div className={cn.demoField}>{demoValue}</div>
        </FieldWrapper>
      ) : (
        [
          'stream_custom_period',
          'stream_custom_days',
          'stream_custom_weeks',
        ].includes(tariff?.type) && (
          <>
            {hasOnePeriod() ? (
              <FieldWrapper label="Длительность">
                <div className={cn.field}>{getOnePeriodValue()}</div>
              </FieldWrapper>
            ) : (
              <>
                {!!tariff?.isCheckboxesView ? (
                  <FieldWrapper label="Выберите период оплаты">
                    <div className={cn.checkboxList}>
                      {getCheckboxValues()?.map((item, index) => (
                        <CheckboxLabel
                          label={item?.label ?? ''}
                          key={index}
                          value={isChecked(value, index)}
                          onChange={() => setValue(Number(item?.value))}
                        />
                      ))}
                    </div>
                  </FieldWrapper>
                ) : (
                  <SliderField
                    label={getSliderLabel()}
                    value={value}
                    extraContent={value?.toString()}
                    step={null}
                    min={getPeriodsMin()}
                    max={getPeriodsMax()}
                    marks={getSliderMarks()}
                    onChange={setValue}
                  />
                )}
              </>
            )}
          </>
        )
      )}
      {tariff?.type === 'month' && (
        <SliderField
          label={getSliderLabel()}
          value={value}
          extraContent={value?.toString()}
          min={getTariffValueMin(tariff)}
          max={getSliderMax()}
          onChange={setValue}
        />
      )}
      {(tariff?.type === 'group_per_lesson' || ['hour', 'minute'].includes(tariff?.type)) && (
        <>
          {tariff?.is_custom_tariff_values ? (
            <>
              {hasOnePeriod() ? (
                <FieldWrapper label="Количество">
                  <div className={cn.field}>{getOnePeriodValue()}</div>
                </FieldWrapper>
              ) : (
                <SliderField
                  label={getSliderLabel()}
                  value={value}
                  extraContent={value?.toString()}
                  step={null}
                  min={getPeriodsMin()}
                  max={getPeriodsMax()}
                  marks={getSliderMarks()}
                  onChange={setValue}
                />
              )}
            </>
          ) : (
            <SliderField
              label={getSliderLabel()}
              value={value}
              extraContent={value?.toString()}
              min={getTariffValueMin(tariff)}
              max={getSliderMax()}
              onChange={setValue}
            />
          )}
        </>
      )}
      {tariff?.has_demo && (
        <CheckboxLabel
          label={'Активировать демо период'}
          value={demo}
          onChange={onChangeDemo}
        />
      )}
    </div>
  )
}

export const getTariffValueMin = (tariff: ICourseTariff): number => {
  if (
    tariff?.type === 'group_per_lesson' ||
    (['hour', 'minute'].includes(tariff?.type) && tariff.is_custom_tariff_values)
  ) {
    if (!!tariff.is_custom_tariff_values) {
      return Math.min(
        ...(tariff?.custom_tariff_values?.map((item) => item?.value) ?? [0]),
      )
    } else {
      return 1
    }
  }

  switch (tariff?.type) {
    case 'stream_custom_period': {
      const values = getPositiveNumbers(
        tariff?.tariffPeriods?.map((item) => item.months),
      )
      return values.length ? Math.min(...values) : 1
    }
    case 'stream_custom_days': {
      const values = getPositiveNumbers(
        tariff?.tariffDays?.map((item) => item.days),
      )
      return values.length ? Math.min(...values) : 1
    }
    case 'stream_custom_weeks': {
      const values = getPositiveNumbers(
        tariff?.tariffWeeks?.map((item) => item.weeks),
      )
      return values.length ? Math.min(...values) : 1
    }

    default:
      return 1
  }
}

export const isIndividualTariff = (tariff?: ICourseTariff | null): boolean =>
  ['hour', 'minute'].includes(tariff?.type ?? '')

/**
 * Значение по умолчанию для тарифа.
 * У поминутного индивидуального тарифа это стандартная длительность занятия из
 * настроек тарифа — тогда цена по умолчанию равна стоимости стандартного занятия.
 * У остальных тарифов (в том числе почасового, где значение — количество занятий)
 * поведение прежнее: минимально допустимое значение.
 */
export const getTariffDefaultValue = (tariff: ICourseTariff): number => {
  const minValue = getTariffValueMin(tariff)

  if (tariff?.type !== 'minute' || tariff?.is_custom_tariff_values) {
    return minValue
  }

  const configuredDuration = Number(tariff?.lesson_duration)
  const lessonDuration =
    Number.isFinite(configuredDuration) && configuredDuration > 0
      ? configuredDuration
      : 60

  return Math.max(minValue, lessonDuration)
}

export const normalizeTariffValue = (
  tariff: ICourseTariff,
  value?: number | string | null,
): number => {
  const numericValue = Number(value)
  const minValue = getTariffValueMin(tariff)

  if (!Number.isFinite(numericValue) || numericValue < minValue) {
    return minValue
  }

  return numericValue
}

export default SelectTariffValue
